import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ReactComponent as LogoSiberat } from '../../svg/logo.svg';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Backdrop,
  CircularProgress,
  IconButton,
  InputAdornment,
  OutlinedInput
} from '@mui/material';
import * as Yup from 'yup';
import { Icon } from '@iconify/react';
import validateToken from '../../features/forgot-password/validateToken';
import resetPasswordForgot from '../../features/forgot-password/changePassword';
import usePageTitle from '../../hooks/usePageTitle';

const schema = Yup.object().shape({
  password: Yup.string().required('Password is required')
});

const ResetPassword = () => {
  usePageTitle('Reset Password');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [loading, setLoading] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema)
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  const handleMouseUpPassword = event => {
    event.preventDefault();
  };

  const resetPassword = (data, e) => {
    e.preventDefault();
    setLoading(true);
    resetPasswordForgot(data, token, navigate, setLoading);
    e.stopPropagation();
  };

  useEffect(() => {
    if (!token) return;
    validateToken(token, navigate, setLoading);
  }, [token, navigate]);

  return (
    <div className='login-page min-h-screen pb-16 flex flex-col'>
      <header className='mb-[35px]'>
        <div className='flex justify-between px-5 pt-5'>
          <div className='my-auto w-[50px] md:w-[75px]'>
            <LogoSiberat />
          </div>
        </div>
      </header>

      <Backdrop
        sx={theme => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
        open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>

      <section className='flex items-center justify-center container mx-auto mt-6 md:mt-auto content-center flex-1'>
        <div
          id='image-login'
          className='hidden md:flex flex-initial w-1/2 items-center justify-end pr-4 lg:pr-8'>
          <img
            src='/images/img-forgot-multimodal.png'
            alt='Pengiriman alat berat via truck dan kapal'
            className='auth-hero-img object-contain'
          />
        </div>

        <div id='form-login' className='flex-initial m-auto p-5 max-w-[440px] w-full'>
          <h1 className='text-[32px] md:text-[35px] font-bold text-[#1B2124] leading-tight m-0'>
            Create a new password
          </h1>
          <p className='text-left text-sm my-3 font-normal text-[#6B7280]'>
            Choose a strong password for your SiBerat Customer account.
          </p>

          <form onSubmit={handleSubmit(resetPassword)} className='text-sm'>
            <label
              htmlFor='password'
              className='font-medium block mb-1.5 mt-6 text-[#4F4F4F]'>
              New password
            </label>
            <OutlinedInput
              placeholder='Enter a new password'
              type={showPassword ? 'text' : 'password'}
              size='small'
              id='password'
              name='password'
              {...register('password')}
              className={`input-login w-full ${
                errors.password ? 'border-red-500' : ''
              }`}
              startAdornment={
                <InputAdornment position='start'>
                  <Icon
                    icon='solar:lock-password-bold'
                    width={18}
                    color='#888888'
                  />
                </InputAdornment>
              }
              endAdornment={
                <InputAdornment position='end'>
                  <IconButton
                    aria-label={
                      showPassword
                        ? 'hide the password'
                        : 'display the password'
                    }
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    onMouseUp={handleMouseUpPassword}
                    edge='end'
                    className='show-password'>
                    <Icon
                      icon={
                        showPassword
                          ? 'solar:eye-closed-bold'
                          : 'solar:eye-bold'
                      }
                      width={18}
                      color='#6D6D6D'
                    />
                  </IconButton>
                </InputAdornment>
              }
            />
            {errors.password && (
              <p className='text-red-500 text-xs italic mt-1'>
                {errors.password.message}
              </p>
            )}

            <ul className='list-disc ml-5 mt-4 text-[#6B7280] text-xs space-y-1'>
              <li>At least 8 characters</li>
              <li>Use a mix of letters, numbers, and symbols</li>
            </ul>

            <button
              type='submit'
              className='btn-primary-siberat w-full h-12 py-3 px-4 mt-8 focus:outline-none'>
              Save new password
            </button>
          </form>
        </div>
      </section>

      <footer className='mt-auto px-5 py-5'>
        <div className='font-medium text-xs text-[#9CA3AF]'>
          &copy; {new Date().getFullYear()} PT. SiBerat Digital Logistik, All
          Rights Reserved.
        </div>
      </footer>
    </div>
  );
};

export default ResetPassword;
